import clsx from 'clsx';
import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {
  Body,
  Button,
  Headline,
  Icon,
  PoppaysLoader,
  designSystemToken,
} from '@lightricks/react-design-system';
import {
  CONTENT_SUBMISSION_ANALYTICS_SCREENS as ANALYTICS_SCREENS,
  CONTENT_SUBMISSION_ANALYTICS_BUTTONS as ANALYTICS_BUTTONS,
} from '@/lib/analytics/analyticsConstants';
import buildOffPlatformShareUrl from '@/utils/buildOffPlatformShareUrl';
import raiseFlashMessage from '@/utils/raiseFlashMessage';
import translate from '@/utils/translate';
import {PHASE, Phase} from '@/config/contentSubmissionPhases';
import {ContentSubmission} from '@/types/contentSubmission';
import {
  CONTENT_SUBMISSION_SORT,
  ContentSubmissionSort,
  type SortOption,
} from '@/types/sorting';
import {VIEW_TYPE_TABLE, VIEW_TYPES, ViewType} from '@/types/viewType';
import campaignUpdaters from '@/api/updaters/campaign';
import ContentSubmissionsGrid from '@/views/campaigns/components/content-submissions/content-submissions-grid';
import ContentSubmissionsTable from '@/views/campaigns/components/content-submissions/content-submissions-table';
import TabContentLayout from '@/views/campaigns/components/tab-content-layout';
import {
  CONTENT_SUBMISSION_ACTION,
  type ContentSubmissionAction,
} from '@/components/content-submission-card/contentSubmissionCardTypes';
import {SortByDropdown} from '@/components/dropdown';
import FilterButton from '@/components/filters/filter-button';
import Sidebar from '@/components/sidebar';
import TypeSelector from '@/components/type-selector';
import useTrackButtonEvent from '@/hooks/analytics/use-track-button-event/useTrackButtonEvent';
import useTrackScreenEvent from '@/hooks/analytics/use-track-screen-event/useTrackScreenEvent';
import useDashboardUserQuery from '@/hooks/queries/use-dashboard-user-query';
import useContentSubmissionLink from '@/hooks/use-content-submission-link';
import {contentSubmissionsActions} from '@/stores/content/content-submissions/contentSubmissionsStore';
import styles from './ContentSubmissionsTab.module.scss';
import CreatorsFilter from './components/creators-filter';
import FilterDrawer from './components/filter-drawer';
import useContentSubmissionsTabState from './hooks/useContentSubmissionsTabState';

type ContentSubmissionsTabProps = {
  isOffPlatform?: boolean;
};

const ACTION_TO_BUTTON_NAME: Record<ContentSubmissionAction, string> = {
  [CONTENT_SUBMISSION_ACTION.ACCEPT]: ANALYTICS_BUTTONS.ACTION_APPROVE,
  [CONTENT_SUBMISSION_ACTION.REJECT]: ANALYTICS_BUTTONS.ACTION_REQUEST_EDITS,
  [CONTENT_SUBMISSION_ACTION.ARCHIVE]: ANALYTICS_BUTTONS.ACTION_ARCHIVE,
  [CONTENT_SUBMISSION_ACTION.UNARCHIVE]: ANALYTICS_BUTTONS.ACTION_UNARCHIVE,
  [CONTENT_SUBMISSION_ACTION.SHARE]: ANALYTICS_BUTTONS.ACTION_SHARE,
  [CONTENT_SUBMISSION_ACTION.DOWNLOAD]: ANALYTICS_BUTTONS.ACTION_DOWNLOAD,
  [CONTENT_SUBMISSION_ACTION.RUN_AI_ANALYSIS]:
    ANALYTICS_BUTTONS.ACTION_RUN_AI_ANALYSIS,
  [CONTENT_SUBMISSION_ACTION.VIEW_AI_ANALYSIS]:
    ANALYTICS_BUTTONS.ACTION_VIEW_AI_ANALYSIS,
  [CONTENT_SUBMISSION_ACTION.RESIZE]: ANALYTICS_BUTTONS.ACTION_RESIZE,
  [CONTENT_SUBMISSION_ACTION.COMMENT]: ANALYTICS_BUTTONS.ACTION_COMMENT,
};

function ContentSubmissionsTab({
  isOffPlatform = false,
}: ContentSubmissionsTabProps) {
  const {
    campaignId,
    campaign,
    campaignCreators,
    contentSubmissions,
    isLoading,
    isLoadingMeta,
    sidebarItems,
    selectedPhase,
    isOnAllTab,
    setSelectedPhase,
    isFilterDrawerOpen,
    activeFilterCount,
    activeFiltersKey,
    phaseTotalCount,
    guardedFetchNextPage,
    isFetchingNextPage,
    viewType,
    setViewType,
    sortBy,
    sortDir,
    setSorting,
  } = useContentSubmissionsTabState();

  const sortOptions = useMemo<SortOption[]>(
    () => [
      {id: CONTENT_SUBMISSION_SORT.KIND, label: translate('Content Type')},
      {id: CONTENT_SUBMISSION_SORT.VERSIONS_COUNT, label: translate('Version')},
      {id: CONTENT_SUBMISSION_SORT.AASM_STATE, label: translate('Status')},
      {id: CONTENT_SUBMISSION_SORT.OVERDUE, label: translate('Overdue')},
      {id: CONTENT_SUBMISSION_SORT.UPLOAD_DATE, label: translate('Uploaded')},
    ],
    []
  );

  const {dashboardUser} = useDashboardUserQuery({});
  const isAdmin = dashboardUser?.isAdmin;

  const {presented, dismissed} = useTrackScreenEvent();
  const {pressed: pressedButton} = useTrackButtonEvent();

  const showDownloadAll = selectedPhase === PHASE.ACCEPTED && !isOffPlatform;
  const [isDownloadingAll, setIsDownloadingAll] = useState(false);

  useEffect(() => {
    if (isFilterDrawerOpen) {
      presented({
        screen_name: ANALYTICS_SCREENS.ASSET_FILTER_DRAWER,
        source: ANALYTICS_SCREENS.ASSETS,
        tab: campaignId,
      });
    } else {
      dismissed({
        screen_name: ANALYTICS_SCREENS.ASSET_FILTER_DRAWER,
        destination: ANALYTICS_SCREENS.ASSETS,
        tab: campaignId,
      });
    }
  }, [isFilterDrawerOpen, campaignId]);

  const isTableView = viewType === VIEW_TYPE_TABLE;

  const tableSorting = sortBy ? [{id: sortBy, desc: sortDir === 'desc'}] : [];
  const handleSortingChange = useCallback(
    (newSorting: {id: string; desc: boolean}[]) => {
      if (newSorting.length > 0) {
        const {id, desc} = newSorting[0];
        const dir = desc ? 'desc' : 'asc';
        pressedButton({
          button_name: ANALYTICS_BUTTONS.SORT_CHANGE,
          screen_name: ANALYTICS_SCREENS.ASSETS_TABLE,
          campaign_id: campaignId,
          tab: `${id}--${dir}`,
        });
        setSorting(id as ContentSubmissionSort, dir);
      } else {
        setSorting(undefined, undefined);
      }
    },
    [setSorting, campaignId]
  );

  // Clear filters when navigating away from the tab.
  useEffect(() => {
    return () => {
      contentSubmissionsActions.clearAndApplyFilters();
    };
  }, []);

  const scrollRef = useRef<HTMLDivElement>(null);
  const getLink = useContentSubmissionLink();
  const getCardLink = useCallback(
    (cs: ContentSubmission) =>
      cs.kind === 'mixed_media'
        ? getLink({parentContentSubmissionId: cs.id})
        : getLink({contentSubmissionId: cs.id}),
    [getLink]
  );

  const handleShareAll = useCallback(async () => {
    if (!campaignId) {
      return;
    }

    pressedButton({
      button_name: ANALYTICS_BUTTONS.SHARE_ALL,
      screen_name: isTableView
        ? ANALYTICS_SCREENS.ASSETS_TABLE
        : ANALYTICS_SCREENS.ASSETS_GRID,
      campaign_id: campaignId,
    });

    try {
      await navigator.clipboard.writeText(
        buildOffPlatformShareUrl(
          campaignId,
          undefined,
          campaign?.offPlatformShareToken
        )
      );
      raiseFlashMessage({
        status: 'success',
        message: translate('Share URL copied to clipboard'),
      });
    } catch {
      raiseFlashMessage({
        status: 'error',
        message: translate('Failed to copy URL'),
      });
    }
  }, [campaignId, isTableView, campaign?.offPlatformShareToken]);

  const handleDownloadAll = useCallback(async () => {
    if (!campaignId) {
      return;
    }

    pressedButton({
      button_name: ANALYTICS_BUTTONS.DOWNLOAD_ALL,
      screen_name: isTableView
        ? ANALYTICS_SCREENS.ASSETS_TABLE
        : ANALYTICS_SCREENS.ASSETS_GRID,
      campaign_id: campaignId,
    });

    setIsDownloadingAll(true);
    try {
      await campaignUpdaters.downloadContent({campaignId, approvedOnly: true});
      raiseFlashMessage({
        status: 'success',
        message: translate(
          'You will receive an email with a link to your download in a few minutes.'
        ),
      });
    } catch {
      raiseFlashMessage({
        status: 'error',
        message: translate('Failed to start download. Please try again.'),
      });
    } finally {
      setIsDownloadingAll(false);
    }
  }, [campaignId, isTableView]);

  if (isLoadingMeta) {
    return <PoppaysLoader />;
  }

  return (
    <TabContentLayout
      sidebar={
        <Sidebar
          title={translate('Phase')}
          items={sidebarItems}
          selectedId={selectedPhase}
          onSelect={(id) => {
            pressedButton({
              button_name: ANALYTICS_BUTTONS.PHASE_TAB,
              screen_name: ANALYTICS_SCREENS.ASSETS_GRID,
              tab: id as string,
              campaign_id: campaignId,
            });
            setSelectedPhase(id as Phase);
          }}
        />
      }
    >
      <div className={styles.contentWrapper}>
        {/* Off-platform reviewers don't have the global app chrome
            (BrandHeader / breadcrumbs) that normally surfaces the campaign
            name, so we render the name here at the top of the right column.
            Hidden in the authenticated app to avoid duplicating the header. */}
        {isOffPlatform && campaign?.name ? (
          <Headline size="lg" className={styles.offPlatformCampaignTitle}>
            {campaign.name}
          </Headline>
        ) : null}
        <div className={styles.controlsBar}>
          {isOnAllTab && (
            <FilterButton
              onClick={() => {
                pressedButton({
                  button_name: ANALYTICS_BUTTONS.FILTER_TOGGLE,
                  screen_name: ANALYTICS_SCREENS.ASSETS_GRID,
                  campaign_id: campaignId,
                });
                if (isFilterDrawerOpen) {
                  contentSubmissionsActions.closeFilterDrawer();
                } else {
                  contentSubmissionsActions.openFilterDrawer();
                }
              }}
              activeFilterCount={activeFilterCount}
            />
          )}
          <CreatorsFilter
            campaignCreators={campaignCreators}
            onCreatorSelected={() =>
              pressedButton({
                button_name: ANALYTICS_BUTTONS.CREATOR_FILTER,
                screen_name: ANALYTICS_SCREENS.ASSETS_GRID,
                campaign_id: campaignId,
              })
            }
          />
          <div className={styles.toolbarRightSection}>
            <Body size="sm" className={styles.showingCount}>
              {translate('Showing {count}', {count: phaseTotalCount ?? 0})}
            </Body>
            <hr className={styles.toolbarSeparator} />
            {!isTableView ? (
              <SortByDropdown
                options={sortOptions}
                sortBy={sortBy}
                sortDir={sortDir}
                onChange={(id, dir) => {
                  pressedButton({
                    button_name: ANALYTICS_BUTTONS.SORT_CHANGE,
                    screen_name: ANALYTICS_SCREENS.ASSETS_GRID,
                    campaign_id: campaignId,
                    tab: `${id}--${dir}`,
                  });
                  setSorting(id as ContentSubmissionSort, dir);
                }}
              />
            ) : null}
            <Button
              className={styles.shareAllButton}
              size="small"
              mode="plain"
              appearance="neutral"
              disabled={!isOffPlatform && !campaign?.offPlatformShareToken}
              onClick={handleShareAll}
              startIcon={
                <Icon size="small" appearance="neutral" name="Actions-Share" />
              }
            >
              {translate('Share All')}
            </Button>
            {showDownloadAll ? (
              <Button
                className={styles.downloadAllButton}
                size="small"
                mode="plain"
                appearance="neutral"
                onClick={handleDownloadAll}
                disabled={isDownloadingAll || !phaseTotalCount}
                startIcon={
                  <Icon
                    size="small"
                    appearance="neutral"
                    name="Navigation-Download"
                  />
                }
              >
                {translate('Download All')}
              </Button>
            ) : null}
            <TypeSelector
              types={VIEW_TYPES}
              selectedTypeId={viewType}
              onChange={(id) => {
                if (id === viewType) {
                  return;
                }
                pressedButton({
                  button_name: ANALYTICS_BUTTONS.VIEW_TYPE_CHANGE,
                  screen_name: ANALYTICS_SCREENS.ASSETS,
                  campaign_id: campaignId,
                  tab: id,
                });
                setViewType(id as ViewType);
              }}
              backgroundColor={designSystemToken(
                'semantic.bg.neutral-inverse-secondary'
              )}
              selectionColor={designSystemToken('reference.gray.300')}
            />
          </div>
        </div>
        <div
          ref={scrollRef}
          className={clsx(
            styles.scrollContainer,
            isTableView && styles.scrollContainerTable
          )}
        >
          {isTableView ? (
            <ContentSubmissionsTable
              campaignId={campaignId!}
              contentSubmissions={contentSubmissions}
              campaignNetwork={campaign?.network}
              campaignState={campaign?.state ?? campaign?.aasmState}
              selectedPhase={selectedPhase}
              getCardLink={getCardLink}
              activeFiltersKey={activeFiltersKey}
              onEndReached={guardedFetchNextPage}
              isLoadingInitial={isLoading}
              isLoadingMore={isFetchingNextPage}
              isAdmin={isAdmin}
              isOffPlatform={isOffPlatform}
              sorting={tableSorting}
              onSortingChange={handleSortingChange}
              screenName={ANALYTICS_SCREENS.ASSETS_TABLE}
              onRowClick={({creator_id, flow_id}) =>
                pressedButton({
                  button_name: ANALYTICS_BUTTONS.ASSET_CARD,
                  screen_name: ANALYTICS_SCREENS.ASSETS_TABLE,
                  campaign_id: campaignId,
                  creator_id,
                  flow_id,
                })
              }
              onRowMenuOpen={({creator_id, flow_id}) =>
                pressedButton({
                  button_name: ANALYTICS_BUTTONS.ASSET_CARD_MENU_OPEN,
                  screen_name: ANALYTICS_SCREENS.ASSETS_TABLE,
                  campaign_id: campaignId,
                  creator_id,
                  flow_id,
                })
              }
              onRowActionSelected={(action, {creator_id, flow_id}) =>
                pressedButton({
                  button_name: ACTION_TO_BUTTON_NAME[action] ?? action,
                  screen_name: ANALYTICS_SCREENS.ASSETS_TABLE,
                  campaign_id: campaignId,
                  creator_id,
                  flow_id,
                })
              }
            />
          ) : (
            <ContentSubmissionsGrid
              contentSubmissions={contentSubmissions}
              campaignNetwork={campaign?.network}
              campaignState={campaign?.state ?? campaign?.aasmState}
              getCardLink={getCardLink}
              activeFiltersKey={activeFiltersKey}
              phaseTotalCount={phaseTotalCount}
              sortBy={sortBy}
              sortDir={sortDir}
              selectedPhase={selectedPhase}
              onEndReached={guardedFetchNextPage}
              isLoadingInitial={isLoading}
              isLoadingMore={isFetchingNextPage}
              scrollRef={scrollRef}
              isAdmin={isAdmin}
              isOffPlatform={isOffPlatform}
              onCardClick={({creator_id, flow_id}) =>
                pressedButton({
                  button_name: ANALYTICS_BUTTONS.ASSET_CARD,
                  screen_name: ANALYTICS_SCREENS.ASSETS_GRID,
                  campaign_id: campaignId,
                  creator_id,
                  flow_id,
                })
              }
              onCardMenuOpen={({creator_id, flow_id}) =>
                pressedButton({
                  button_name: ANALYTICS_BUTTONS.ASSET_CARD_MENU_OPEN,
                  screen_name: ANALYTICS_SCREENS.ASSETS_GRID,
                  campaign_id: campaignId,
                  creator_id,
                  flow_id,
                })
              }
              onCardActionSelected={(action, {creator_id, flow_id}) =>
                pressedButton({
                  button_name: ACTION_TO_BUTTON_NAME[action] ?? action,
                  screen_name: ANALYTICS_SCREENS.ASSETS_GRID,
                  campaign_id: campaignId,
                  creator_id,
                  flow_id,
                })
              }
            />
          )}
        </div>
      </div>

      <FilterDrawer
        campaignCreators={campaignCreators}
        campaignSocialNetwork={campaign?.network}
        campaignApprovalSteps={campaign?.campaignApprovalSteps}
        onApply={() =>
          pressedButton({
            button_name: ANALYTICS_BUTTONS.FILTER_APPLY,
            screen_name: ANALYTICS_SCREENS.ASSET_FILTER_DRAWER,
            campaign_id: campaignId,
          })
        }
        onClearAll={() =>
          pressedButton({
            button_name: ANALYTICS_BUTTONS.FILTER_CLEAR_ALL,
            screen_name: ANALYTICS_SCREENS.ASSET_FILTER_DRAWER,
            campaign_id: campaignId,
          })
        }
        onContentTypeChange={(value) =>
          pressedButton({
            button_name: ANALYTICS_BUTTONS.FILTER_CONTENT_TYPE,
            screen_name: ANALYTICS_SCREENS.ASSET_FILTER_DRAWER,
            campaign_id: campaignId,
            tab: value,
          })
        }
        onStatusChange={(value) =>
          pressedButton({
            button_name: ANALYTICS_BUTTONS.FILTER_STATUS,
            screen_name: ANALYTICS_SCREENS.ASSET_FILTER_DRAWER,
            campaign_id: campaignId,
            tab: value,
          })
        }
        onCreatorChange={({creator_id}) =>
          pressedButton({
            button_name: ANALYTICS_BUTTONS.CREATOR_FILTER,
            screen_name: ANALYTICS_SCREENS.ASSET_FILTER_DRAWER,
            campaign_id: campaignId,
            creator_id,
          })
        }
      />
    </TabContentLayout>
  );
}

export default ContentSubmissionsTab;
